/* Created by Interactive 63 - https://interactive63.com */
/**
 * Curved motion between position keyframes. A keyframe may carry handles that bend the path into
 * and out of it, as cubic Bézier tangents. Progress along a curve follows its length, so the
 * segment's easing alone decides the speed, however long the handles are.
 */
import { isVec2, type Keyframe, type MotionHandles, type Vec2 } from '../model/types';

/** Points measured along a curve to map progress to distance travelled. */
const LENGTH_SAMPLES = 48;
/** How much of the distance to the neighbouring keyframes new smooth handles reach. */
const SMOOTH_HANDLE_SHARE = 1 / 3;
/** Small time step, in ms, used to read the direction of travel. */
const DIRECTION_STEP_MS = 1;
const EPSILON = 1e-9;

/** The four control points of a curved segment. */
type Bezier = readonly [Vec2, Vec2, Vec2, Vec2];

const lengthTables = new WeakMap<Keyframe, { to: Keyframe; table: number[] }>();

/** Whether a handle has any length. */
function hasLength(handle: Vec2 | undefined): handle is Vec2 {
  return handle !== undefined && Math.hypot(handle.x, handle.y) > EPSILON;
}

/** Whether the segment from one keyframe to the next follows a curve rather than a straight line. */
export function isCurvedSegment(from: Keyframe, to: Keyframe): boolean {
  return (
    isVec2(from.value) &&
    isVec2(to.value) &&
    from.easing.type !== 'hold' &&
    (hasLength(from.motion?.out) || hasLength(to.motion?.in))
  );
}

/** The control points of a segment; a missing handle sits on its keyframe. */
function bezierOf(from: Keyframe, to: Keyframe): Bezier {
  const p0 = from.value as Vec2;
  const p3 = to.value as Vec2;
  const out = from.motion?.out ?? { x: 0, y: 0 };
  const into = to.motion?.in ?? { x: 0, y: 0 };
  return [p0, { x: p0.x + out.x, y: p0.y + out.y }, { x: p3.x + into.x, y: p3.y + into.y }, p3];
}

/** The point on a cubic Bézier at parameter t. */
function pointAt(b: Bezier, t: number): Vec2 {
  const u = 1 - t;
  const w0 = u * u * u;
  const w1 = 3 * u * u * t;
  const w2 = 3 * u * t * t;
  const w3 = t * t * t;
  return {
    x: w0 * b[0].x + w1 * b[1].x + w2 * b[2].x + w3 * b[3].x,
    y: w0 * b[0].y + w1 * b[1].y + w2 * b[2].y + w3 * b[3].y,
  };
}

/** The direction of a cubic Bézier at parameter t, falling back to the chord where it vanishes. */
function tangentAt(b: Bezier, t: number): Vec2 {
  const u = 1 - t;
  const x =
    3 * u * u * (b[1].x - b[0].x) + 6 * u * t * (b[2].x - b[1].x) + 3 * t * t * (b[3].x - b[2].x);
  const y =
    3 * u * u * (b[1].y - b[0].y) + 6 * u * t * (b[2].y - b[1].y) + 3 * t * t * (b[3].y - b[2].y);
  if (Math.hypot(x, y) > EPSILON) return { x, y };
  return { x: b[3].x - b[0].x, y: b[3].y - b[0].y };
}

/** Cumulative length at evenly spaced parameters, cached per segment. */
function lengthTable(from: Keyframe, to: Keyframe, b: Bezier): number[] {
  const cached = lengthTables.get(from);
  if (cached && cached.to === to) return cached.table;
  const table = [0];
  let previous = b[0];
  for (let i = 1; i <= LENGTH_SAMPLES; i += 1) {
    const point = pointAt(b, i / LENGTH_SAMPLES);
    table.push((table[i - 1] ?? 0) + Math.hypot(point.x - previous.x, point.y - previous.y));
    previous = point;
  }
  lengthTables.set(from, { to, table });
  return table;
}

/** The curve parameter at which a share of the total length has been travelled. */
function parameterForShare(table: readonly number[], share: number): number {
  const total = table[table.length - 1] ?? 0;
  if (total < EPSILON) return share;
  const target = share * total;
  let i = 1;
  while (i < table.length - 1 && (table[i] ?? 0) < target) i += 1;
  const before = table[i - 1] ?? 0;
  const after = table[i] ?? before;
  const within = after - before < EPSILON ? 0 : (target - before) / (after - before);
  return (i - 1 + within) / LENGTH_SAMPLES;
}

/**
 * The position at eased progress p along a curved segment. Progress outside 0..1, from
 * overshooting easings, carries on along the end tangent.
 */
export function curvePosition(from: Keyframe, to: Keyframe, p: number): Vec2 {
  const b = bezierOf(from, to);
  const table = lengthTable(from, to, b);
  const total = table[table.length - 1] ?? 0;
  if (p < 0 || p > 1) {
    const end = p < 0 ? b[0] : b[3];
    const tangent = tangentAt(b, p < 0 ? 0 : 1);
    const length = Math.hypot(tangent.x, tangent.y);
    const over = (p < 0 ? p : p - 1) * total;
    if (length < EPSILON) return end;
    return { x: end.x + (tangent.x / length) * over, y: end.y + (tangent.y / length) * over };
  }
  return pointAt(b, parameterForShare(table, p));
}

/** Handles reversed for playing a segment backwards: what arrived now leaves, and the reverse. */
export function reversedHandles(motion: MotionHandles): MotionHandles {
  return {
    ...(motion.out ? { in: motion.out } : {}),
    ...(motion.in ? { out: motion.in } : {}),
    ...(motion.corner ? { corner: true } : {}),
  };
}

/**
 * Smooth handles for the keyframe at `index`: pointing along the line from the previous keyframe
 * to the next, each reaching a third of the way to its neighbour. The first and last keyframes
 * get a single handle towards their only neighbour.
 */
export function smoothHandles(keyframes: readonly Keyframe[], index: number): MotionHandles {
  const here = keyframes[index]?.value;
  const before = keyframes[index - 1]?.value;
  const after = keyframes[index + 1]?.value;
  if (!isVec2(here)) return {};
  const prev = isVec2(before) ? before : undefined;
  const next = isVec2(after) ? after : undefined;
  const through = {
    x: (next ?? here).x - (prev ?? here).x,
    y: (next ?? here).y - (prev ?? here).y,
  };
  const size = Math.hypot(through.x, through.y);
  if (size < EPSILON) return {};
  const unit = { x: through.x / size, y: through.y / size };
  const reach = (other: Vec2 | undefined): number =>
    other ? Math.hypot(other.x - here.x, other.y - here.y) * SMOOTH_HANDLE_SHARE : 0;
  const outLength = reach(next);
  const inLength = reach(prev);
  return {
    ...(prev ? { in: { x: -unit.x * inLength, y: -unit.y * inLength } } : {}),
    ...(next ? { out: { x: unit.x * outLength, y: unit.y * outLength } } : {}),
  };
}

/** The opposite handle of a smooth keyframe: pointing the other way, keeping its own length. */
export function mirroredHandle(moved: Vec2, opposite: Vec2 | undefined): Vec2 | undefined {
  const length = Math.hypot(moved.x, moved.y);
  if (!opposite || length < EPSILON) return opposite;
  const keep = Math.hypot(opposite.x, opposite.y);
  return { x: (-moved.x / length) * keep, y: (-moved.y / length) * keep };
}

/**
 * The angle of travel, in degrees, of a position sampler at a time: measured over a short step
 * ahead, or behind at the end. Returns null where the element is not moving.
 */
export function travelAngle(
  sample: (time: number) => Vec2 | undefined,
  time: number,
): number | null {
  const here = sample(time);
  const ahead = sample(time + DIRECTION_STEP_MS);
  const behind = sample(time - DIRECTION_STEP_MS);
  if (!here) return null;
  const moving = (a: Vec2 | undefined, b: Vec2): a is Vec2 =>
    a !== undefined && Math.hypot(b.x - a.x, b.y - a.y) > EPSILON;
  if (ahead && moving(here, ahead)) {
    return (Math.atan2(ahead.y - here.y, ahead.x - here.x) * 180) / Math.PI;
  }
  if (moving(behind, here)) {
    return (Math.atan2(here.y - behind.y, here.x - behind.x) * 180) / Math.PI;
  }
  return null;
}
